import { API, authedFetch } from "./api";
import { bookingSnippet } from "./bookingTokens";
import { genSampleToken } from "./sampleClinicians";

const tokenKey = (scope) => "simplyrefer:demo-tokens:" + scope;
const tourKey = (scope) => "simplyrefer:demo-tour:" + scope;
const ACCOUNT_SESSION_KEY = "simplyrefer:account-demo-session";
const linkSessionKey = (accessToken) => "simplyrefer:link-demo-session:" + accessToken.split(".")[0];

const newBrowserSessionId = () => {
  if (window.crypto && typeof window.crypto.randomUUID === "function") return window.crypto.randomUUID();
  return "browser-" + Date.now() + "-" + Math.random().toString(36).slice(2);
};

const readJson = (key, fallback) => {
  try {
    const value = sessionStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (key, value) => {
  sessionStorage.setItem(key, JSON.stringify(value));
};

export const getDemoTokens = (scope) => readJson(tokenKey(scope), []);

export const getDemoTourStep = (scope) => {
  const value = Number(sessionStorage.getItem(tourKey(scope)));
  return Number.isFinite(value) && value >= 0 ? value : 0;
};

export const setDemoTourStep = (scope, step) => {
  sessionStorage.setItem(tourKey(scope), String(step));
  window.dispatchEvent(new CustomEvent("simplyrefer-demo-tour", { detail: { scope, step } }));
};

export const restartDemoTour = (scope) => setDemoTourStep(scope, 0);
export const skipDemoTour = (scope) => setDemoTourStep(scope, 99);

export const saveDemoToken = (scope, clinician, clinicName) => {
  const token = genSampleToken();
  const record = {
    id: newBrowserSessionId(),
    token,
    created_at: new Date().toISOString(),
    gp_clinic_name: clinicName,
    clinician: {
      id: clinician.id,
      first_name: clinician.first_name,
      last_name: clinician.last_name,
      profession: clinician.profession,
      specialty: clinician.specialty,
      suburb: clinician.suburb,
      state: clinician.state,
      email: clinician.email,
    },
  };
  record.snippet = bookingSnippet({ token, clinician });
  const existing = getDemoTokens(scope);
  writeJson(tokenKey(scope), [record, ...existing]);
  window.dispatchEvent(new CustomEvent("simplyrefer-demo-tokens", { detail: { scope } }));
  return record;
};

export const clearDemoStorage = (scope) => {
  sessionStorage.removeItem(tokenKey(scope));
  sessionStorage.removeItem(tourKey(scope));
};

export async function ensureAccountDemoSession() {
  const existing = readJson(ACCOUNT_SESSION_KEY, null);
  if (existing && existing.session_id) return existing;
  const response = await authedFetch("/demo/sample/session", {
    method: "POST",
    body: JSON.stringify({ browser_session_id: newBrowserSessionId() }),
  });
  if (!response.ok) return null;
  const session = await response.json();
  writeJson(ACCOUNT_SESSION_KEY, session);
  return session;
}

export async function trackAccountDemoEvent(sessionId, eventType, pagePath, eventData = {}) {
  if (!sessionId) return;
  try {
    await authedFetch("/demo/sample/sessions/" + sessionId + "/events", {
      method: "POST",
      body: JSON.stringify({ event_type: eventType, page_path: pagePath, event_data: eventData }),
    });
  } catch {
    // Demo tracking is best-effort and must never block the product experience.
  }
}

export const getStoredLinkDemoSession = (accessToken) => readJson(linkSessionKey(accessToken), null);

export async function startLinkDemoSession(accessToken) {
  const existing = getStoredLinkDemoSession(accessToken);
  if (existing && existing.session_id) return existing;
  const response = await fetch(API + "/demo/access/" + encodeURIComponent(accessToken) + "/session", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ browser_session_id: newBrowserSessionId() }),
  });
  if (!response.ok) throw new Error("Could not start this demo invitation");
  const session = await response.json();
  writeJson(linkSessionKey(accessToken), session);
  return session;
}

export async function trackLinkDemoEvent(accessToken, sessionId, eventType, pagePath, eventData = {}) {
  if (!accessToken || !sessionId) return;
  try {
    await fetch(API + "/demo/access/" + encodeURIComponent(accessToken) + "/sessions/" + sessionId + "/events", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event_type: eventType, page_path: pagePath, event_data: eventData }),
      keepalive: true,
    });
  } catch {
    // Demo tracking is best-effort and must never block the product experience.
  }
}

export async function endLinkDemoSession(accessToken, sessionId, scope) {
  if (scope) clearDemoStorage(scope);
  sessionStorage.removeItem(linkSessionKey(accessToken));
  if (!accessToken || !sessionId) return;
  try {
    await fetch(API + "/demo/access/" + encodeURIComponent(accessToken) + "/sessions/" + sessionId + "/end", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
    });
  } catch {
    // Closing a browser can interrupt this best-effort request.
  }
}
